import { Box, Typography, useMediaQuery, Theme, useTheme } from "@mui/material";
import React, { useEffect, useRef } from "react";
import houseImage from "../../../../assets/sellWithUs/topBackgroundHouse.png";
import Arrow from "../../../../assets/icons/arrow.svg";
import RocketIcon from "../../../../assets/icons/rocket.svg";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useFormattedSocialProof } from "../../../../hooks/useFormattedSocialProof";
import { GalleryBlocks } from "./GalleryBlocks";
import { HeroImgStatisticsSwiperDownMd } from "./HeroImgStatisticsSwiperDownMd";
import { TopGalleryHeroImgStatisticsUpMd } from "./TopGalleryHeroImgStatisticsUpMd";

export default function TopGallery() {
  const navigate = useNavigate();
  const { t } = useTranslation(["sellWithUs"]);
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const downLg = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));
  const downMd = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const { successfulProjects } = useFormattedSocialProof();

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(to left, rgba(255, 255, 255, 0.25) -100%, rgba(5, 5, 5, 0.25) 36.34%, rgba(0, 0, 0, 0.25) 37%), url(${houseImage})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: { xs: "auto", md: "cover" },
          backgroundPosition: { xs: "top", md: "center" },
          minHeight: { xs: "auto", md: "600px" },
          maxHeight: "790px",
          mx: { xs: "20px", md: "20px" },
          display: "flex",
          flexDirection: {
            xs: "column",
            md: isRTL ? "row-reverse" : "row",
          },
          justifyContent: { xs: "flex-start", md: "space-between" },
          alignItems: {
            xs: isRTL ? "flex-end" : "flex-start",
            md: "flex-end",
          },
          position: "relative",
          borderRadius: "20px",
          pt: { xs: "65px", md: "65px" },
          pb: { xs: "20px", md: "45px" },
        }}
      >
        {/* Text Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            color: "white",
            gap: { xs: "15px", md: "20px" },
            fontFamily: isRTL ? "Noto Sans Hebrew" : "DM sans",
            textAlign: isRTL ? "right" : "left",
            mb: { xs: "60px", md: "0" },
            alignSelf: isMobile && isRTL ? "flex-end" : "",
            padding: { xs: "20px", md: "30px" },
            maxWidth: "430px",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: isRTL ? "flex-end" : "flex-start",
              background: "#FFFFFF52",
              backdropFilter: "blur(40px)",
              borderRadius: "7px",
              p: "8px 16px",
              width: "fit-content",
              gap: "8px",
              alignSelf: isRTL ? "flex-end" : "flex-start",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            <img
              src={RocketIcon}
              alt="rocket"
              style={{ width: "16px", height: "16px" }}
            />
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: "500",
                fontFamily: isRTL ? "Noto Sans Hebrew" : "DM sans",
                color: "white",
              }}
            >
              {successfulProjects}+
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "12px", md: "16px" },
                fontWeight: "400",
                fontFamily: isRTL ? "Noto Sans Hebrew" : "DM sans",
                color: "white",
                textTransform: "uppercase",
              }}
            >
              {t("successful")} {t("projects")}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: "500",
              fontFamily: isRTL ? "Noto Sans Hebrew" : "DM sans",
              lineHeight: "18px",
            }}
          >
            {t("start")}
          </Typography>
          <Box>
            <Typography
              sx={{
                fontSize: { xs: "20px", sm: "24px", md: "36px" },
                fontWeight: "600",
                textTransform: "uppercase",
                lineHeight: 1.2,
                fontFamily: isRTL ? "Noto Sans Hebrew" : "Frank Ruhl Libre",
                mb: "10px",
                direction: i18n.dir(),
              }}
            >
              {t("maximize")}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "12px", md: "18px" },
                fontWeight: "400",
                fontFamily: isRTL ? "Noto Sans Hebrew" : "DM sans",
              }}
            >
              {t("ai")}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: isRTL ? "flex-end" : "flex-start",
              color: "#fff",
              background: "linear-gradient(to right, #034A73, #0A7992)",
              p: downLg ? "10px 18px" : "15px 25px",
              cursor: "pointer",
              borderRadius: "24px",
              width: "fit-content",
              gap: "8px",
              flexDirection: isRTL ? "row-reverse" : "row",
              alignSelf: isRTL ? "flex-end" : "flex-start",
            }}
            onClick={() => navigate("/contact-us")}
          >
            <Typography
              sx={{
                mr: isRTL ? "12px" : "0",
                ml: isRTL ? "0" : "12px",
                fontFamily: isRTL ? "Noto Sans Hebrew" : "DM Sans",
                fontWeight: isRTL ? 400 : 500,
                lineHeight: "16px",
                fontSize: downLg ? "14px" : "16px",
              }}
            >
              {t("partner")}
            </Typography>
            <img
              src={Arrow}
              alt="arrow"
              style={{
                transform: isRTL ? "scaleX(-1)" : "scaleX(1)",
                width: "16px",
                height: "16px",
              }}
            />
          </Box>
        </Box>

        {downMd ? (
          <HeroImgStatisticsSwiperDownMd />
        ) : (
          <TopGalleryHeroImgStatisticsUpMd />
        )}
      </Box>

      {/* Images Slider */}
      <GalleryBlocks />
    </>
  );
}
